import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { AppLoader } from 'components/bootstrap-loader/AppLoader/AppLoader'
import { useIsBootstrapLoading } from 'components/bootstrap-loader/use-bootstrap-loading'

/**
 * A single, always-mounted full-screen loading overlay.
 *
 * The <AppLoader> element is rendered unconditionally — it never unmounts —
 * so its animation never restarts. Visibility is toggled via opacity
 * and pointer-events so the loading signal from multiple gates can hand off
 * without causing a re-mount glitch.
 *
 * The overlay defaults to visible until the app's first paint settles. Gates
 * register their real loading state in useEffect (after first paint), so
 * without this the very first commit would paint a grey screen. The `settled`
 * flag bridges that one-frame gap: it is false on the first render (overlay
 * visible) and flips true in the same effects flush that registers the real
 * gate keys — React 18 batches the two state updates into one re-render so
 * visible never dips to false between first-paint and gate registration.
 *
 * Mount this once at the app root (App.tsx) as a sibling of RouterProvider.
 * Individual bootstrap gates register their loading state via
 * useRegisterBootstrapLoading instead of rendering their own loaders.
 */
export const GlobalBootstrapOverlay: React.FC = () => {
  const isLoading = useIsBootstrapLoading()
  const [settled, setSettled] = useState(false)

  // Clear the pre-settle default after the first paint. By this point every
  // gate's useEffect has also run and registered its real loading state.
  useEffect(() => {
    setSettled(true)
  }, [])

  const visible = isLoading || !settled

  return (
    <Overlay $visible={visible} aria-hidden={!visible}>
      <AppLoader playing={visible} />
    </Overlay>
  )
}

const Overlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: center;
  padding-top: 200px;
  background: #ffffff;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  pointer-events: ${(p) => (p.$visible ? 'auto' : 'none')};
`
